// eyeq — QA reviewer tool contracts (eyeq.weern.com #/qa, auth class 'qa':
// role 'qa' or 'manager' by set membership, never by rank — ranking qa above
// lead would hand reviewers the lead-class team surface).
//
// The queue is the bulk intake (source_pipeline 'eyeq-intake', status 'qa' —
// the one enum slot nothing else reads or writes, so 56k imported frames stay
// invisible to the manager overview, the url-backfill counter and the link
// channel). Stage 1 only: accept / PII / reject / move-to-correct-cell. A
// stage-1 accept keeps status 'qa' with qa_verdict 'accepted'; promotion to
// 'accepted' belongs to stage 2 (link verification), which this tool stubs.
import { z } from 'zod';

// ── GET /api/eyeq/qa/summary ────────────────────────────────────────────────
// One aggregation scan: `cells` carries every non-empty (locale, category,
// sub_category) queue bucket; the client folds it into the home grid, the
// subcategory list and the review-screen country chips. `tree` is the full
// taxonomy (including empty cells) for the reassign sheet, in seed order.
export const EyeqQaCellSchema = z.object({
  locale: z.string(),
  category: z.string(),
  sub_category: z.string(),
  queued: z.number().int(),   // unreviewed intake frames in this bucket
  decided: z.number().int(),  // intake frames here that already carry a verdict
});
export type EyeqQaCell = z.infer<typeof EyeqQaCellSchema>;

export const EyeqQaTreeSubSchema = z.object({
  sub_category: z.string(),
  url_required: z.boolean(),
  plan: z.number().int(), // sum over locales of the quota effective target
});
export const EyeqQaTreeCatSchema = z.object({
  category: z.string(),
  subs: z.array(EyeqQaTreeSubSchema),
});
export type EyeqQaTreeCat = z.infer<typeof EyeqQaTreeCatSchema>;

export const EyeqQaSummaryResSchema = z.object({
  pending: z.number().int(),     // total unreviewed intake frames
  links: z.number().int(),       // stage-2 queue size; 0 until stage 2 ships
  ready: z.number().int(),       // intake frames with qa_verdict 'accepted'
  shipped: z.number().int(),     // status 'shipped' across the catalog
  plan_total: z.number().int(),  // sum of quota effective targets
  cells: z.array(EyeqQaCellSchema),
  tree: z.array(EyeqQaTreeCatSchema),
});
export type EyeqQaSummaryRes = z.infer<typeof EyeqQaSummaryResSchema>;

// ── GET /api/eyeq/qa/queue?category=&sub_category=&locale=&limit=&after= ────
// Keyset paging by asset_id: the unreviewed set shrinks while two reviewers
// work, so offset paging silently skips frames. `after` is exclusive; ''
// starts from the lowest id. Decided frames stay in the page (their badges
// must survive a reload), so the filter is scope, not verdict.
export const EyeqQaQueueQuerySchema = z.object({
  category: z.string().optional(),
  sub_category: z.string().optional(),
  locale: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(100),
  after: z.string().default(''),
  // 1 = only frames without a verdict (the working queue); 0 = everything in
  // scope, used when re-opening a session to restore badges.
  unreviewed: z.coerce.number().int().min(0).max(1).default(1),
});
export type EyeqQaQueueQuery = z.infer<typeof EyeqQaQueueQuerySchema>;

export const EyeqQaQueueItemSchema = z.object({
  asset_id: z.string(),
  locale: z.string(),
  filename: z.string(),
  category: z.string(),
  sub_category: z.string(),
  // What the annotation pipeline decided at import; differs from the current
  // cell after a 'move' — the review panel's «Было определено» row.
  ai_category: z.string(),
  ai_sub_category: z.string(),
  qa_verdict: z.enum(['', 'accepted', 'rejected']),
  reject_reason: z.string(),
});
export type EyeqQaQueueItem = z.infer<typeof EyeqQaQueueItemSchema>;

export const EyeqQaQueueResSchema = z.object({
  items: z.array(EyeqQaQueueItemSchema),
  total: z.number().int(), // rows matching the scope filter (before paging)
});
export type EyeqQaQueueRes = z.infer<typeof EyeqQaQueueResSchema>;

// GET /api/eyeq/qa/frame/:asset_id/image?size=thumb|prev|full — binary
// endpoint: R2 sibling <r2_key>.thumb.jpg → <r2_key>.prev.jpg → original,
// best available at or above the asked tier. Query contract only, no res
// schema per contracts.md.
export const EyeqQaImageSizeSchema = z.enum(['thumb', 'prev', 'full']);

// ── POST /api/eyeq/qa/decide ────────────────────────────────────────────────
// One click = one decision (the design's four buttons):
//   accept → qa_verdict 'accepted', status stays 'qa'
//   pii    → qa_verdict 'rejected', reject_reason 'pii', status 'rejected'
//   reject → qa_verdict 'rejected', reject_reason 'other', status 'rejected'
//   move   → meta cell + cat_id updated, then treated as accept; the import
//            values stay in ai_category/ai_sub_category
// The UPDATE is conditional on qa_verdict = '' — with two reviewers on one
// queue, the second decision on a frame gets 409 {error:'already_decided'}
// instead of silently overwriting the first.
export const EyeqQaDecisionSchema = z.enum(['accept', 'pii', 'reject', 'move']);
export type EyeqQaDecision = z.infer<typeof EyeqQaDecisionSchema>;

export const EyeqQaDecideReqSchema = z.object({
  asset_id: z.string().min(1),
  decision: EyeqQaDecisionSchema,
  // move only — both required together (enforced in the handler so the
  // refusal names the field); MEQ 'and' spelling.
  category: z.string().trim().min(1).max(80).optional(),
  sub_category: z.string().trim().min(1).max(120).optional(),
});
export type EyeqQaDecideReq = z.infer<typeof EyeqQaDecideReqSchema>;

export const EyeqQaDecideResSchema = z.object({
  ok: z.boolean(),
  asset_id: z.string(),
  qa_verdict: z.enum(['accepted', 'rejected']),
  reject_reason: z.string(),
  category: z.string(),
  sub_category: z.string(),
  qa_at: z.string(),
});
export type EyeqQaDecideRes = z.infer<typeof EyeqQaDecideResSchema>;

// ── GET /api/eyeq/qa/week ───────────────────────────────────────────────────
// The reviewer's own 7-day tape, computed from eyeq_audit (qa_* actions by
// actor) — NOT from qa_at, which is overwritten on re-decide and cleared by
// undo, so streaks built on it would lie.
export const EyeqQaWeekDaySchema = z.object({
  date: z.string(), // YYYY-MM-DD, UTC
  count: z.number().int(),
});
export const EyeqQaWeekResSchema = z.object({
  days: z.array(EyeqQaWeekDaySchema).length(7), // oldest first, ends today
  streak: z.number().int(),                     // consecutive non-zero days ending today or yesterday
  best: EyeqQaWeekDaySchema.nullable(),         // null until any decision exists
});
export type EyeqQaWeekRes = z.infer<typeof EyeqQaWeekResSchema>;

// ── POST /api/eyeq/upload/intakeImport (machine) ────────────────────────────
// Bulk registration of already-uploaded intake frames. The worker owns every
// derived field a Python fork would otherwise duplicate: asset_id/uuid
// minting, cat_id, country from the locale, place_details assembly and
// day-phase outdoor lighting. Idempotent: an asset whose r2_key is already
// indexed is reported in `skipped`, not re-inserted.
export const EyeqIntakeImportRowSchema = z.object({
  r2_key: z.string().min(1).max(500),
  locale: z.string().min(1),
  category: z.string().min(1),     // MEQ 'and' spelling
  sub_category: z.string().min(1),
  subject_name: z.string().max(300),
  disambiguation_details: z.string().max(3000),
  url: z.string().max(2048).default(''),
  has_text: z.boolean(),
  indoor_outdoor: z.string(),
  indoor_lighting: z.string().default(''),
  place_type: z.string().default(''),
  camera_make: z.string().max(80),
  camera_model: z.string().max(80),
  captured_at: z.string().max(40).default(''), // EXIF 'YYYY:MM:DD HH:MM:SS'
  gps_present: z.union([z.literal(0), z.literal(1)]),
  width: z.number().int().positive(),
  height: z.number().int().positive(),
  bytes: z.number().int().positive(),
  dhash: z.string().default(''),
});
export type EyeqIntakeImportRow = z.infer<typeof EyeqIntakeImportRowSchema>;

export const EyeqIntakeImportReqSchema = z.object({
  rows: z.array(EyeqIntakeImportRowSchema).min(1).max(50),
});
export type EyeqIntakeImportReq = z.infer<typeof EyeqIntakeImportReqSchema>;

export const EyeqIntakeImportResSchema = z.object({
  ok: z.boolean(),
  inserted: z.number().int(),
  skipped: z.number().int(), // r2_key already indexed
  errors: z.array(z.object({ r2_key: z.string(), note: z.string() })),
});
export type EyeqIntakeImportRes = z.infer<typeof EyeqIntakeImportResSchema>;
